import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { LETTER_FORMAT } from '../../utils/constants'
import Question from '../../models/question'
import { questionServiceParser } from '../../../../helpers/question-parser'
import Skill from '../../models/skill'
import SkillItem from '../../models/skill_item'
import map from 'lodash/map'
import markdownParser from '../../../../helpers/markdown-parser'
import { resetStyleMathLive, checkMathLiveExist, createMathliveField } from '../../../../lib/mathlive'
import { checkIOS } from '../../utils/utils'
import Exam from '../../models/exam'
import { processAudioSpeak } from '../../../../lib/speak_polly'
import * as AceEditor from 'brace'
import 'brace/mode/javascript'
import 'brace/mode/typescript'
import 'brace/mode/ruby'
import 'brace/mode/python'
import 'brace/mode/html'
import 'brace/mode/json'
import 'brace/mode/sql'
import 'brace/theme/monokai'

export interface ExamTestQuestionBodyProps {
  exam: Exam
  currentQuestion: Question
  index: number
  time: number
}

export function useExamTestQuestionBody(
  props: ExamTestQuestionBodyProps,
  emit: (event: 'changeValue', value: string[], question: Question) => void
) {
  const value = ref<string[]>([])
  const text = ref('')
  // Audioのため、mounted1/10秒のあとに、viewを実行します。やらないと、IOSで体重問題発生します。
  const show = ref(true)

  const radioValue = computed((): string => {
    return value.value.length ? value.value[0] : ''
  })

  const content = computed((): string => {
    if (!props.currentQuestion) {
      return ''
    }
    setTimeout(() => {
      if (props.currentQuestion.isTextfield) {
        (document
          .getElementById(`question_${props.currentQuestion.id}`)
          ?.querySelectorAll('.textfield-question') as any)?.forEach((element: HTMLInputElement, index: number) => {
            element.value = value.value[index] ? value.value[index] : ''
            const answerItem = props.currentQuestion.answerItems[index]
            if (checkMathLiveExist(answerItem.body)) {
              createMathliveField(element)
            }
          })
      }
      if (props.currentQuestion.isInlineSelect) {
        (document
          .getElementById(`question_${props.currentQuestion.id}`)
          ?.querySelectorAll('.inline-select-question') as any)?.forEach((element: HTMLSelectElement, index: number) => {
            Array.prototype.forEach.call(element.options, (option: HTMLOptionElement) => {
              if (option.value == value.value[index]) {
                option.selected = true
              }
            })
          })
      }

      // Text - Speak 実行
      // Use lazy mode (loadWhenPlay=true): set a default src immediately and fetch the real
      // audio only on the user's play click. Eager loading set the src outside a user gesture,
      // so on iOS Safari the <audio> stayed locked after navigating to the next question and
      // would not play; it also accumulated loaded media elements until the browser limit was hit.
      const el = document.getElementById(`question_${props.currentQuestion.id}`)
      if (el) {
        processAudioSpeak(el, true)
      }

    }, 100)
    const contentBody = props.currentQuestion.body.split('<a ').join('<a target="_blank" ')
    return questionServiceParser(contentBody, props.currentQuestion, true)
  })

  const explanation = computed((): string => {
    if (!props.currentQuestion.explanation) {
      return ''
    }
    return markdownParser(props.currentQuestion.explanation)
  })

  const questionTemporaryAnswer = computed(() => {
    return `${props.exam.userExam.id}_temporary_q_${props.currentQuestion.id}_textarea`
  })

  const emitChangeValue = () => {
    emit('changeValue', value.value, props.currentQuestion)
  }

  const updateData = () => {
    const tmpText = localStorage.getItem(questionTemporaryAnswer.value)
    value.value = props.currentQuestion.answerValues
    if (tmpText) {
      text.value = tmpText ? tmpText : ''
    } else if (value.value.length) {
      text.value = value.value[0]
    } else {
      text.value = ''
    }

    emitChangeValue()
  }

  watch(() => props.currentQuestion, () => {
    updateData()
  }, { immediate: true })

  const textareaOnChange = () => {
    emit('changeValue', [text.value], props.currentQuestion)
  }

  onMounted(() => {
    updateData()
    resetStyleMathLive()
    nextTick(() => setupAceEditor())
    forceAudioReflowIOS()
  })

  // IOSでは v-html で挿入された <audio> が reflow されるまで正しく描画されないことがある。
  // 以前は v-if(show) でコンテンツ全体を100ms隠して再描画していたが、それが原因で
  // 回答中・問題遷移時にちらついていた。代わりに <audio> 要素だけを同期的に reflow させる（ちらつかない）。
  const forceAudioReflowIOS = () => {
    if (!checkIOS()) return
    nextTick(() => {
      const el = document.getElementById(`question_${props.currentQuestion?.id}`)
      el?.querySelectorAll('audio').forEach((node) => {
        const audio = node as HTMLElement
        audio.style.display = 'none'
        void audio.offsetHeight // 強制リフロー
        audio.style.display = ''
      })
    })
  }

  const skillItemsName = (skill: Skill): string => {
    const skillItems = skill.skillItems.filter(skillItem =>
      props.currentQuestion.skillItems.some(item => item.id == skillItem.id)
    )
    return getSkillsName(skillItems)
  }

  const getSkillsName = (skills: Skill[] | SkillItem[]) => {
    return map(skills, 'name').join(', ')
  }

  watch(text, () => {
    localStorage.setItem(questionTemporaryAnswer.value, text.value)
  }, { immediate: true })

  const callBackChange = (val: string[]) => {
    value.value = val
    emitChangeValue()
  }

  const callBackChangeOne = (a: string) => {
    value.value = [a]
    emitChangeValue()
  }

  const getAnswerMode = (): string | null => {
    const match = props.currentQuestion.body.match(/code,\s?"?(\w+)"?]/)
    const code = match ? match[1] : ''
    return code && ["html", "javascript", "typescript", "ruby", "python", "json", "sql"].includes(code.toLowerCase()) ? code : null
  }

  const setupAceEditor = () => {
    const mode = getAnswerMode()
    if (!mode) return

    const editor = AceEditor.edit("editor")
    editor.getSession().setMode(`ace/mode/${mode}`)
    editor.setTheme('ace/theme/monokai')
    editor.setValue(text.value, 1)
    editor.setOptions({ fontSize: "16px" })
    editor.on('change', (e) => {
      text.value = editor.getSession().getValue()
      emit('changeValue', [text.value], props.currentQuestion)
    })
  }

  return {
    value,
    text,
    show,
    radioValue,
    content,
    explanation,
    questionTemporaryAnswer,
    textareaOnChange,
    callBackChange,
    callBackChangeOne,
    getAnswerMode,
    skillItemsName,
    getSkillsName,
    markdownParser,
    LETTER_FORMAT
  }
}
